import { ProductItemsView, productSummary } from './ProductItemsField'
import { orderHasChanged } from './orderRevision'
import { formatDecimalValue } from './formModel'
import { useAuth } from '@/context/AuthContext'
import { useCrmHub, useCrmSubscription } from '@/context/SignalRContext'
import { editorRoles } from '@/helpers/roles'
import { affectsRecord } from '@/services/workspaceRefresh'
import { collectOrderJson, OrderFormPages } from '@/components/forms/OrderFieldInputs'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Accordion, Alert, Button, Modal } from 'react-bootstrap'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { fieldCaption, formPages, parseOrderAnswers, parseStoredForm, tableLines, type FormPage, type OrderField, type OrderForm } from '@/components/forms/formModel'
import { readError } from '@/services/apiClient'
import { amendOrder, loadStoredOrder, type StoredOrder } from '@/services/formService'

export function StoredOrderDialog({ orderId, onHide, footer, onChanged }: { orderId: number | null; onHide: () => void; footer?: ReactNode; onChanged?: () => void }) {
  useCrmSubscription(orderId == null ? null : `order:${orderId}`)
  const { session } = useAuth()
  const { lastEvent } = useCrmHub()
  const [order, setOrder] = useState<StoredOrder | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const seenEvent = useRef(lastEvent)
  const editingBaseline = useRef<StoredOrder | null>(null)
  const [changedElsewhere, setChangedElsewhere] = useState(false)
  const definition = useMemo(() => order ? parseStoredForm(storedFormJson(order), { name: order.formName ?? '' }) : null, [order])
  const canEdit = !!session?.role && editorRoles.includes(session.role) && order && ['Submitted', 'Processing'].includes(order.status)

  useEffect(() => {
    setOrder(null); setError(null); setEditing(false); setChangedElsewhere(false)
    if (orderId == null) return
    let cancelled = false
    loadStoredOrder(orderId).then(row => { if (!cancelled) setOrder(row) })
      .catch(caught => { if (!cancelled) setError(readError(caught)) })
    return () => { cancelled = true }
  }, [orderId])

  useEffect(() => {
    const newEvent = seenEvent.current !== lastEvent
    seenEvent.current = lastEvent
    if (orderId == null || !affectsRecord(lastEvent, 'orders', 'order', orderId)) return
    if (editing && !newEvent) return
    let cancelled = false
    loadStoredOrder(orderId).then(row => {
      if (cancelled) return
      if (editing) {
        const baseline = editingBaseline.current
        if (baseline) setChangedElsewhere(orderHasChanged(baseline, row))
      } else setOrder(row)
    })
      .catch(caught => { if (!cancelled) setError(readError(caught)) })
    return () => { cancelled = true }
  }, [lastEvent, orderId, editing])

  async function save(element: HTMLFormElement) {
    if (!order) return
    setBusy(true); setError(null)
    try {
      await amendOrder(order.id, collectOrderJson(element), order.dateModified)
      const updated = await loadStoredOrder(order.id)
      setOrder(updated); setEditing(false); setChangedElsewhere(false); onChanged?.()
    } catch (caught) { setError(readError(caught)) }
    finally { setBusy(false) }
  }

  return (
    <>
    {orderId != null && order && !editing && createPortal(
      <article className="submission-print" aria-hidden="true">
        <h1>{order.formName || 'Submission'} · #{order.id}</h1>
        <p><strong>Customer:</strong> {order.customerName || 'Customer'}<br />
          <strong>Submitted:</strong> {new Date(order.placedAt).toLocaleString()}{order.placedByName ? ` · ${order.placedByName}` : ''}<br />
          <strong>Status:</strong> {order.status}{order.formVersionNumber > 0 ? ` · Form version ${order.formVersionNumber}` : ''}</p>
        {definition ? formPages(definition).map((page, index) => <section key={page.id}>
          {formPages(definition).length > 1 && <h2>{page.title?.trim() || `Page ${index + 1}`}</h2>}
          <PageBody page={page} definition={definition} answers={parseOrderAnswers(order.orderJson)} />
        </section>) : <AnswerList answers={parseOrderAnswers(order.orderJson)} />}
      </article>, document.body)}
    <Modal show={orderId != null} onHide={() => { if (!busy) onHide() }} size="lg" backdrop={editing ? 'static' : true} keyboard={!editing}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>{editing ? 'Amend' : 'Submission'} #{orderId} · {order?.formName || 'Submission'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {orderId != null && !order && !error && <LoadingSpinner text="Loading submission" size="2x" />}
        {error && <Alert variant="danger">{error}</Alert>}
        {order && <>
          <div className="d-flex flex-wrap justify-content-between gap-2 text-body-secondary mb-3">
            <span>{order.status} · {order.placedByName} · {new Date(order.placedAt).toLocaleString()}</span>
            {order.formVersionNumber > 0 && <span className="ms-auto" aria-label={`Form version ${order.formVersionNumber}`}>v{order.formVersionNumber}</span>}
          </div>
          {editing && definition ? <>
            <p>Update this submission before it is processed. The submission number stays the same.</p>
            {changedElsewhere && <Alert variant="info">This submission has changed since you started editing. Your entries are preserved; saving checks that this submission is still current.</Alert>}
            <OrderFormPages key={`${order.id}:${order.dateModified}`} form={definition} answers={parseOrderAnswers(order.orderJson)} busy={busy} cataloguePricing={{ formId: order.formId, formVersionId: order.formVersionId, customerId: order.customerId, orderId: order.id, resourceId: session?.billingResourceId, accountId: session?.billingAccountId }}
              submitLabel="Save amendment" onSubmit={element => void save(element)} onCancel={() => { setEditing(false); setError(null) }} />
          </> : <StoredOrderAnswers order={order} />}
        </>}
      </Modal.Body>
      {order && !editing && <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => window.print()}>Print</Button>
        {canEdit && definition && <Button variant="outline-primary" onClick={() => { editingBaseline.current = order; setEditing(true); setChangedElsewhere(false); setError(null) }}>Edit submission</Button>}
        {footer}
      </Modal.Footer>}
    </Modal>
    </>
  )
}

export function storedFormJson(order: Pick<StoredOrder, 'renderSnapshotJson' | 'formVersionJson'>) {
  const snapshot = order.renderSnapshotJson?.trim()
  return snapshot ? order.renderSnapshotJson : order.formVersionJson
}

export function StoredOrderAnswers({ order, description = 'Latest saved answers, using the original form.' }: { order: Pick<StoredOrder, 'id' | 'orderJson' | 'renderSnapshotJson' | 'formVersionJson' | 'formName'>; description?: string }) {
  const definition = parseStoredForm(storedFormJson(order), { name: order.formName ?? '' })
  const answers = parseOrderAnswers(order.orderJson)
  return (
    <>
      <p className="small text-body-secondary">{description}</p>
      {definition?.notifyEmail?.trim() && <p>Submission email address: {definition.notifyEmail}</p>}
      {definition ? <OrderAnswers key={order.id} definition={definition} answers={answers} /> : <AnswerList answers={answers} />}
    </>
  )
}

export function orderDetailsText(order: StoredOrder): string {
  const definition = parseStoredForm(storedFormJson(order), { name: order.formName ?? '' })
  const answers = parseOrderAnswers(order.orderJson)
  const lines = [
    order.customerName || 'Customer',
    order.formName || 'Submission',
    order.placedAt ? new Date(order.placedAt).toLocaleString() : '',
    order.placedByName ? `Submitted by ${order.placedByName}` : '',
    definition?.notifyEmail?.trim() ? `Submission email address: ${definition.notifyEmail.trim()}` : '',
    '',
  ].filter((line, index) => line !== '' || index === 5)
  if (!definition) {
    for (const [key, value] of Object.entries(answers)) lines.push(`${key}: ${displayAnswer(value)}`)
    return lines.join('\n')
  }
  const pages = formPages(definition)
  for (const page of pages) {
    if (pages.length > 1) lines.push(page.title?.trim() || 'Page', '')
    const ids = page.rows.flatMap((row) => row.columns.flatMap((column) => column.fieldIds))
    for (const id of ids) {
      const field = definition.fields.find((item) => item.id === id)
      if (!field || field.type === 'heading' || field.type === 'title' || field.type === 'label') {
        if (field) lines.push(fieldCaption(field))
        continue
      }
      if (field.type === 'table') {
        if (field.label.trim()) lines.push(field.label.trim())
        lines.push(...tableAnswerLines(field, answers))
        continue
      }
      const caption = field.label.trim()
      const answer = choiceAnswer(field, answers)
      lines.push(caption ? `${caption}: ${answer}` : answer)
    }
    lines.push('')
  }
  return lines.join('\n')
}

function OrderAnswers({ definition, answers }: { definition: OrderForm; answers: Record<string, string | string[]> }) {
  const pages = formPages(definition)
  if (pages.length < 2) return <PageBody page={pages[0]} definition={definition} answers={answers} />
  return (
    <Accordion className="order-history stored-order-pages">
      {pages.map((page, index) => (
        <Accordion.Item key={page.id} eventKey={page.id}>
          <Accordion.Header>
            {page.title?.trim() || `Page ${index + 1}`}
          </Accordion.Header>
          <Accordion.Body>
            <PageBody page={page} definition={definition} answers={answers} />
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  )
}

function PageBody({ page, definition, answers }: { page: FormPage | undefined; definition: OrderForm; answers: Record<string, string | string[]> }) {
  if (!page) return null
  return (
    <>
      {page.rows.map((row) => (
        <div className="row g-2" key={row.id}>
          {row.columns.map((column) => (
            <div className={`col-12 col-md-${column.width}`} key={column.id}>
              {column.fieldIds.map((id) => {
                const field = definition.fields.find((item) => item.id === id)
                return field ? <StoredField key={field.id} field={field} answers={answers} /> : null
              })}
            </div>
          ))}
        </div>
      ))}
    </>
  )
}

function StoredField({ field, answers }: { field: OrderField; answers: Record<string, string | string[]> }) {
  if (field.type === 'title') return <h2 className="h5 stored-order-label">{fieldCaption(field)}</h2>
  if (field.type === 'heading') return <h3 className="h6 stored-order-label">{fieldCaption(field)}</h3>
  if (field.type === 'label') return <div className="stored-order-label">{fieldCaption(field)}</div>
  if (field.type === 'productItems') return <div><strong>{fieldCaption(field)}</strong><ProductItemsView value={answers[field.id]} /></div>
  if (field.type === 'table') return <StoredTable field={field} answers={answers} />
  const caption = field.label.trim()
  return (
    <div className="stored-order-value stored-order-answer">
      {caption && <div className="stored-order-label">{caption}</div>}
      <AnswerText text={choiceAnswer(field, answers)} />
    </div>
  )
}

function StoredTable({ field, answers }: { field: OrderField; answers: Record<string, string | string[]> }) {
  const lines = tableLines(field)
  const columns = field.columns ?? []
  const caption = field.label.trim()
  return (
    <div className="stored-order-value stored-order-table">
      {caption && <div className="stored-order-label">{caption}</div>}
      <div className="order-product-table-wrap">
        <table className="table table-bordered table-sm order-product-table mb-0">
          <thead>
            <tr>
              <th scope="col" />
              {columns.map((column) => <th key={column.id} scope="col">{fieldCaption(column)}</th>)}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id}>
                <th scope="row">{line.label}</th>
                {columns.map((column) => (
                  <td key={column.id}><AnswerText text={(column.type === 'decimal' ? formatDecimalValue(displayAnswer(answers[`${field.id}.${line.id}.${column.id}`])) : displayAnswer(answers[`${field.id}.${line.id}.${column.id}`]))} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function AnswerText({ text }: { text: string }) {
  return text
}

function tableAnswerLines(field: OrderField, answers: Record<string, string | string[]>): string[] {
  return tableLines(field).flatMap((line) => [
    `  ${line.label}`,
    ...(field.columns ?? []).map((column) => {
      const caption = column.label.trim() || column.name.trim()
      return `    ${caption}: ${(column.type === 'decimal' ? formatDecimalValue(displayAnswer(answers[`${field.id}.${line.id}.${column.id}`])) : displayAnswer(answers[`${field.id}.${line.id}.${column.id}`]))}`
    }),
  ])
}

function AnswerList({ answers }: { answers: Record<string, string | string[]> }) {
  const entries = Object.entries(answers)
  if (!entries.length) return <p>No answers were stored with this order.</p>
  return (
    <dl className="mb-0">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd>{displayAnswer(value)}</dd>
        </div>
      ))}
    </dl>
  )
}

function choiceAnswer(field: OrderField, answers: Record<string, string | string[]>): string {
  if (field.type === 'productItems') return productSummary(answers[field.id])
  if (!['select', 'radio', 'checklist', 'checkbox'].includes(field.type)) return displayAnswer(answers[field.id])
  const selected = answers[field.id]
  const parts = (Array.isArray(selected) ? selected : selected ? [selected] : []).filter((item) => item !== 'Other')
  const other = answers[`${field.id}.other`]
  const typed = Array.isArray(other) ? other.filter(Boolean).join(', ') : other
  if (typed) parts.push(typed)
  return parts.join(', ')
}

function displayAnswer(value: string | string[] | undefined): string {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return ''
  return Array.isArray(value) ? value.join(', ') : value
}
